import React, { useEffect, useState, useRef } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import {
  ActionPill,
  StyledPill
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/components/StyledPill';
import { useBrandTrafficStore } from 'src/zustandStores/BrandTrafficStore';
import { useMainEntityId, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import { BluePlus } from 'src/components/SvgIcons';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Popper from '@mui/material/Popper';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { StyledSwitch } from 'src/components/common/AppSwitch';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useQueryParamValue } from 'src/utils/Hooks/typedHooks';
import { ContentSubTabs } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/utils';
import colors from 'src/utils/colors';

interface BrandFilterPillsProps {
  onBrandClick?: (brandId: number) => void;
  showBrands?: boolean;
  setShowBrands?: (showBrands: boolean) => void;
}

interface BrandSuggestion {
  fieldKey: number;
  value: string;
}

const buttonTextStyle = {
  fontFamily: 'Roboto',
  fontWeight: '500',
  fontSize: '14px',
  padding: '4px 12px',
  cursor: 'default'
};

/**
 * Brand Filter Pills the will be used in the filterable stacked bar chart component (Can be found in the Atlas doris traffic & topics)
 *
 */
const BrandFilterPills = ({ showBrands, setShowBrands }: BrandFilterPillsProps = {}) => {
  const {
    brands,
    selectedBrands,
    isSelectedBrand,
    addBrand,
    removeBrand,
    pickTop4BrandsByVolume,
    getSelectedBrands,
    resetOnEntityChange,
    getBrandColor,
    setUserChangedBrands
  } = useBrandTrafficStore();

  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [searchText, setSearchText] = useState('');
  const addBrandButtonRef = useRef<HTMLButtonElement>(null);
  const subTab = useQueryParamValue('subtab') as ContentSubTabs;
  const entityId = useMainEntityId();
  const mainEntityType = useMainEntityType();
  const theme = useStacklineTheme();

  useEffect(() => {
    if (entityId && mainEntityType && subTab) {
      resetOnEntityChange(mainEntityType, String(entityId), subTab);
    }
  }, [entityId, mainEntityType, resetOnEntityChange, subTab]);

  // Initialize with top brands when no brands are selected
  useEffect(() => {
    if (brands.length > 0 && selectedBrands.length === 0) {
      pickTop4BrandsByVolume();
    }
  }, [brands, selectedBrands.length, pickTop4BrandsByVolume]);

  const selectedBrandsData = getSelectedBrands();

  const availableBrands = brands
    .filter((brand) => !isSelectedBrand(brand.fieldKey))
    .map((brand) => ({
      fieldKey: brand.fieldKey,
      value: brand.metadata.brandName
    }));

  const filteredBrands = searchText
    ? availableBrands.filter((brand) => brand.value.toLowerCase().startsWith(searchText.toLowerCase()))
    : availableBrands;

  // Handle click on a brand pill
  const handleBrandClick = (brandId: number) => {
    if (isSelectedBrand(brandId)) {
      removeBrand(brandId);
      setUserChangedBrands(true);
    }
  };

  const handleAddBrandClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowBrandDropdown((prev) => !prev);
    setSearchText('');
  };

  // Handle brand selection from dropdown
  const handleBrandSelection = (option: BrandSuggestion | null) => {
    if (option) {
      addBrand(option.fieldKey);
    }
    setShowBrandDropdown(false);
    setSearchText('');
  };

  // Handle search text change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  // Handle click away
  const handleClickAway = () => {
    setShowBrandDropdown(false);
    setSearchText('');
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      {showBrands ? (
        <>
          <Flex gap={16} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
            {selectedBrandsData.map((brand, index) => {
              const {
                fieldKey,
                metadata: { brandName }
              } = brand;
              const brandColor = getBrandColor(fieldKey, index);

              return (
                <ActionPill
                  key={fieldKey}
                  variant="filled"
                  onRemovalClick={() => handleBrandClick(fieldKey)}
                  aria-label={`Toggle ${brandName} filter`}
                  tabIndex={0}
                  pillSx={{
                    backgroundColor: hexToRgba(brandColor, 0.1),
                    color: theme.colors.primary,
                    ...buttonTextStyle
                  }}
                  iconSx={{
                    color: brandColor,
                    cursor: 'pointer'
                  }}
                >
                  {brandName}
                </ActionPill>
              );
            })}

            <StyledPill
              variant="outlined"
              aria-label="Toggle Other Brands filter"
              tabIndex={0}
              pillSx={{
                backgroundColor: hexToRgba(theme.colors.primary, 0.1),
                color: theme.colors.primary,
                ...buttonTextStyle
              }}
              iconSx={{
                color: theme.colors.primary
              }}
            >
              Other Brands
            </StyledPill>

            {selectedBrandsData?.length < 4 && selectedBrandsData?.length > 0 && (
              <Button
                ref={addBrandButtonRef}
                onClick={handleAddBrandClick}
                endIcon={<BluePlus style={{ width: 16, height: 16 }} />}
                sx={{
                  backgroundColor: '#fff',
                  border: `1px solid ${hexToRgba(theme.colors.primary, 0.1)}`,
                  borderRadius: '20px',
                  color: theme.colors.primary,
                  height: '28px',
                  textTransform: 'none',

                  ...buttonTextStyle,
                  '&:hover': {
                    backgroundColor: '#f8f8f8'
                  }
                }}
              >
                Add Brands
              </Button>
            )}
          </Flex>

          {/* Brand Autocomplete Dropdown with Popper */}
          <Popper
            open={showBrandDropdown}
            anchorEl={addBrandButtonRef.current}
            placement="bottom-start"
            style={{ zIndex: 1300 }}
          >
            <Paper
              elevation={3}
              sx={{
                marginTop: '8px',
                width: '176px',
                borderRadius: '4px',
                overflow: 'hidden',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
              }}
            >
              {/* Search Input */}
              <Flex sx={{ borderBottom: '1px solid #f0f0f0', padding: '8px 12px' }}>
                <SearchTextField
                  placeholder="Search brands"
                  value={searchText}
                  onChange={handleSearchChange}
                  autoFocus
                  fullWidth
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    '& .MuiInput-root.Mui-disabled': {
                      color: 'red'
                    }
                  }}
                />
              </Flex>

              <Box
                sx={{
                  width: '194px',
                  maxHeight: '184px',
                  overflowY: 'auto'
                }}
              >
                {filteredBrands.map((brand) => (
                  <Box
                    key={brand.fieldKey}
                    sx={{
                      padding: '8px 8px 8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      '&:hover': {
                        backgroundColor: '#f8f9fa'
                      },
                      fontSize: '14px',
                      fontWeight: '500',
                      color: colors.darkBlue,
                      fontFamily: 'Roboto, sans-serif'
                    }}
                    onClick={() => handleBrandSelection(brand)}
                  >
                    {brand.value}
                  </Box>
                ))}
                {filteredBrands.length === 0 && (
                  <Box
                    sx={{
                      padding: '8px 12px',
                      color: colors.grey,
                      fontSize: '14px',
                      fontFamily: 'Roboto, sans-serif',
                      fontStyle: 'italic'
                    }}
                  >
                    No search results found
                  </Box>
                )}
              </Box>
            </Paper>
          </Popper>
        </>
      ) : (
        <div style={{ height: '28px' }}></div>
      )}
      <div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <StyledSwitch
            checked={showBrands}
            onChange={(e) => {
              setShowBrands?.(e.target.checked);
            }}
          />
          <Text variant="subtitle2" sx={{ marginLeft: '8px' }}>
            Show Brands
          </Text>
        </div>
      </div>
    </div>
  );
};

export default BrandFilterPills;
